import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, Phone, Mail } from 'lucide-react';
import { mockData, generateWhatsAppLink } from '../mock';

const ServiceDetail = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();

  // Find the service from mock data
  const service = mockData.services.find(s => s.id === parseInt(serviceId));

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ paddingTop: '100px' }}>
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Program Not Found</h1>
          <Link to="/" className="text-brand-primary hover:text-brand-secondary">
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ paddingTop: '100px' }}>
      {/* Back Button */}
      <div className="container mx-auto px-4 py-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-text-secondary hover:text-brand-primary transition-colors"
        >
          <ArrowLeft size={20} />
          <span>Back</span>
        </button>
      </div>

      {/* Hero Section */}
      <div className="relative">
        <div className="container mx-auto px-4 py-12">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Image */}
            <div className="relative">
              {service.image ? (
                <img 
                  src={service.image}
                  alt={service.title}
                  className="w-full h-[400px] object-cover rounded-3xl"
                  style={{
                    boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
                  }}
                />
              ) : (
                <div 
                  className="w-full h-[400px] rounded-3xl bg-gradient-to-br from-brand-primary/20 to-brand-accent/20 backdrop-blur-sm border border-white/10 flex items-center justify-center overflow-hidden"
                  style={{
                    boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
                  }}
                >
                  <div className="text-center p-8">
                    <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-brand-primary to-brand-accent rounded-full flex items-center justify-center">
                      <span className="text-4xl">
                        {(() => {
                          if (service.icon === 'Dumbbell') return '💪';
                          if (service.icon === 'Smartphone') return '📱';
                          if (service.icon === 'Activity') return '⚡';
                          return '❤️';
                        })()}
                      </span>
                    </div>
                    <p className="text-text-light text-sm">Image coming soon</p>
                    <p className="text-text-muted text-xs mt-2">Placeholder for your custom photo</p>
                  </div>
                </div>
              )}
            </div>

            {/* Content */}
            <div>
              <div className="inline-block px-4 py-2 rounded-full bg-brand-light border border-brand-primary/30 text-brand-primary text-sm font-semibold mb-4">
                Transform With Us
              </div>
              
              <h1 className="text-5xl md:text-6xl font-black mb-6 bg-gradient-to-r from-white via-brand-primary to-brand-accent bg-clip-text text-transparent">
                {service.title}
              </h1>
              
              <p className="text-xl text-text-secondary leading-relaxed mb-8">
                {service.description}
              </p>

              {/* Quick Contact Buttons */}
              <div className="flex flex-wrap gap-4">
                <a
                  href={generateWhatsAppLink(`Hi! I'm interested in ${service.title}`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-cta"
                >
                  Book Consultation
                </a>
                <Link to="/#contact" className="btn-secondary">
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="bg-subtle py-16">
        <div className="container mx-auto px-4">
          <h2 className="section-title text-center mb-12">What You'll Get</h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {(service.benefits || service.outcomes || []).map((benefit, index) => (
              <div
                key={`benefit-${service.id}-${index}-${benefit.substring(0, 20)}`}
                className="network-card p-6 flex items-start gap-4"
              >
                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-brand-primary to-brand-accent flex items-center justify-center flex-shrink-0 mt-1">
                  <Check size={16} className="text-white" />
                </div>
                <p className="text-text-secondary">{benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Pricing Section */}
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-3xl mx-auto">
          <div className="network-card p-12 text-center">
            <h2 className="text-3xl font-bold mb-4 bg-gradient-to-r from-white to-brand-primary bg-clip-text text-transparent">
              Investment in Your Health
            </h2>
            <p className="text-text-secondary mb-8">
              Pricing details available upon consultation. Every program is personalized to your needs and goals.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-8">
              <a
                href={`tel:${mockData.contact.phone}`}
                className="flex items-center gap-3 px-6 py-3 rounded-full bg-white/5 border border-white/10 hover:border-brand-primary/50 transition-all"
              >
                <Phone size={20} className="text-brand-primary" />
                <span className="text-white">{mockData.contact.phone}</span>
              </a>
              
              <a
                href={`mailto:${mockData.contact.email}`}
                className="flex items-center gap-3 px-6 py-3 rounded-full bg-white/5 border border-white/10 hover:border-brand-primary/50 transition-all"
              >
                <Mail size={20} className="text-brand-accent" />
                <span className="text-white">{mockData.contact.email}</span>
              </a>
            </div>

            <a
              href={generateWhatsAppLink(`Hi! I'd like to know more about the pricing for ${service.title}`)}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-cta inline-flex"
            >
              Get Pricing Details via WhatsApp
            </a>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="cta-section">
        <div className="cta-content">
          <h2 className="cta-title">Ready to Transform?</h2>
          <p className="cta-subtitle">
            Take the first step towards your {service.title.toLowerCase()} journey today.
          </p>
          <div className="cta-buttons">
            <a
              href={generateWhatsAppLink(`Hi! I want to start with ${service.title}`)}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-cta"
            >
              Start Your Journey
            </a>
            <Link to="/#contact" className="btn-secondary">
              Learn More
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceDetail;
